import { lazy, Suspense, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuth } from './hooks/useAuth';
import { useUserProfile } from './hooks/useUserProfile';
import { Auth } from './components/Auth';
import { DarkModeProvider } from './contexts/DarkModeContext';
import { FullPageLoader } from './components/FullPageLoader';
import { AppErrorBoundary } from './components/AppErrorBoundary.jsx';
import Topbar from './components/layout/Topbar.jsx';
import MobileNav from './components/layout/MobileNav.jsx';
import { PageContainer } from './components/layout/PageContainer.jsx';

const pick = (key) => (mod) => ({ default: mod[key] || mod.default });

// Lazy load all page components for code splitting
// Only keep critical UI elements (Topbar, Auth, FullPageLoader) as eager imports
const DashboardV2 = lazy(() => import('./features/dashboard/v2/DashboardV2.jsx'));
const PWAInstallPrompt = lazy(() => import('./components/PWAInstallPrompt.jsx').then(pick('PWAInstallPrompt')));
const OfflineIndicator = lazy(() => import('./components/OfflineIndicator.jsx').then(pick('OfflineIndicator')));
const BlockComposer = lazy(() => import('./features/programmer/components/BlockComposer.jsx'));
const RecentWorkouts = lazy(() => import('./features/workouts/RecentWorkouts.jsx'));
const DailyEntryPage = lazy(() => import('./features/entries/DailyEntryPage.jsx'));
const WorkoutProgrammerV2Page = lazy(() => import('./domain/workouts/pages/WorkoutProgrammer.jsx'));
const PRsPage = lazy(() => import('./features/prs/index.js').then(pick('PRsPage')));

const Insights = lazy(() => import('./components/Insights').then(pick('Insights')));
const TrainingCalendar = lazy(() => import('./components/TrainingCalendar').then(pick('TrainingCalendar')));
const WorkoutManager = lazy(() => import('./domain/workouts/pages/WorkoutManager').then(pick('WorkoutManager')));
const WorkoutProgrammer = lazy(() => import('./domain/workouts/pages/WorkoutProgrammer').then(pick('WorkoutProgrammer')));
const WorkoutHistory = lazy(() => import('./domain/workouts/pages/WorkoutHistory').then(pick('WorkoutHistory')));
const WorkoutTemplates = lazy(() => import('./domain/workouts/components/WorkoutTemplates').then(pick('WorkoutTemplates')));
const WorkoutAnalyticsPage = lazy(() => import('./domain/workouts/pages/WorkoutAnalyticsPage'));
const BenchmarkLeaderboards = lazy(() => import('./domain/workouts/pages/BenchmarkLeaderboards'));
const MovementManager = lazy(() => import('./domain/workouts/pages/MovementManager').then(pick('MovementManager')));
const MovementHistoryPage = lazy(() => import('./domain/workouts/pages/MovementHistoryPage'));
const WorkoutSessionDetails = lazy(() => import('./pages/WorkoutSessionDetails'));
const HeartRateMatching = lazy(() => import('./pages/HeartRateMatching'));
const SupplementManager = lazy(() => import('./pages/SupplementManager'));
const SupplementAdherence = lazy(() => import('./pages/SupplementAdherence'));
const LongHorizonPage = lazy(() => import('./pages/LongHorizonPage'));
const UserSettings = lazy(() => import('./components/UserSettings').then(pick('UserSettings')));
const PerformanceDeltaPage = lazy(() => import('./pages/PerformanceDeltaPage'));
const TechniqueAnalyzerPage = lazy(() => import('./pages/TechniqueAnalyzerPage'));
const WearablesSyncPage = lazy(() => import('./pages/WearablesSyncPage'));
const WearablesDashboard = lazy(() => import('./pages/WearablesDashboard'));
const MonthlyReportsPage = lazy(() => import('./pages/MonthlyReportsPage'));
const CoachPage = lazy(() => import('./pages/CoachPage'));
const AdaptiveCoachPage = lazy(() => import('./pages/AdaptiveCoachPage'));
const RealtimeSessionPage = lazy(() => import('./pages/RealtimeSessionPage'));
const MovementBiasPage = lazy(() => import('./pages/MovementBiasPage'));
const NutritionDashboardPage = lazy(() => import('./domain/nutrition/pages/NutritionDashboardPage'));
const DailyNutritionPage = lazy(() => import('./domain/nutrition/pages/DailyNutritionPage'));
const BlockNutritionPage = lazy(() => import('./domain/nutrition/pages/BlockNutritionPage'));
const MealTemplatesPage = lazy(() => import('./domain/nutrition/pages/MealTemplatesPage'));
const DailyTrackingPage = lazy(() => import('./pages/DailyTrackingPage'));
const CompetitionManagerPage = lazy(() => import('./domain/competition/pages/CompetitionManagerPage').then(pick('CompetitionManagerPage')));
const CompetitionEventPage = lazy(() => import('./domain/competition/pages/CompetitionEventPage').then(pick('CompetitionEventPage')));
const CompetitionCreatePage = lazy(() => import('./domain/competition/pages/CompetitionCreatePage').then(pick('CompetitionCreatePage')));
const CompetitionReviewPage = lazy(() => import('./domain/competition/pages/CompetitionReviewPage').then(pick('CompetitionReviewPage')));
const LeaderboardPage = lazy(() => import('./domain/competition/pages/LeaderboardPage').then(pick('LeaderboardPage')));
const BlockAnalyzer = lazy(() => import('./pages/BlockAnalyzer'));
const BenchmarksPage = lazy(() => import('./pages/BenchmarksPage'));
const BenchmarkDetailPage = lazy(() => import('./pages/BenchmarkDetailPage'));
const SharedWorkoutSession = lazy(() => import('./pages/SharedWorkoutSession'));
const ActivityFeedPage = lazy(() => import('./pages/ActivityFeedPage'));
const UserProfilePage = lazy(() => import('./pages/UserProfilePage'));
const DiscoverUsersPage = lazy(() => import('./pages/DiscoverUsersPage'));
const ProfileSetup = lazy(() => import('./components/ProfileSetup').then(pick('ProfileSetup')));
const GymBrowsePage = lazy(() => import('./pages/GymBrowsePage'));
const GymProfilePage = lazy(() => import('./pages/GymProfilePage'));
const GymCreatePage = lazy(() => import('./pages/GymCreatePage'));
const GymSettingsPage = lazy(() => import('./pages/GymSettingsPage'));
const ProgramBrowsePage = lazy(() => import('./domain/programs/pages/ProgramBrowsePage'));
const ProgramDetailPage = lazy(() => import('./domain/programs/pages/ProgramDetailPage'));
const ProgramCreatePage = lazy(() => import('./domain/programs/pages/ProgramCreatePage'));
const ProgramEditPage = lazy(() => import('./domain/programs/pages/ProgramEditPage'));
const MyProgramsPage = lazy(() => import('./domain/programs/pages/MyProgramsPage'));
const MultiProgramCalendar = lazy(() => import('./domain/programs/components/MultiProgramCalendar'));
const ProgramWorkoutDetailPage = lazy(() => import('./domain/programs/pages/ProgramWorkoutDetailPage'));
const ProgramAnalyticsPage = lazy(() => import('./domain/programs/pages/ProgramAnalyticsPage'));
const RoastingApp = lazy(() => import('./features/roasting/RoastingApp'));
const PublicBeanPage = lazy(() => import('./features/roasting/pages/PublicBeanPage'));
const PublicBlendPage = lazy(() => import('./features/roasting/pages/PublicBlendPage'));

// Check if current path is a public route that doesn't require authentication
const isPublicRoute = () => {
  const publicPaths = ['/share/workout/', '/roast/bean/', '/roast/blend/'];
  return publicPaths.some(path => window.location.pathname.startsWith(path));
};

export default function App() {
  const { user, loading, signOut } = useAuth();
  const { profile, hasProfile, loading: profileLoading, refetch } = useUserProfile(user?.id);
  const [showProfileSetup, setShowProfileSetup] = useState(false);

  // Check if we should show profile setup
  // Show it only after profile loading is done and user doesn't have a profile
  const shouldShowSetup = !profileLoading && user && !hasProfile && !showProfileSetup;

  // Handle public routes - render without authentication
  if (isPublicRoute()) {
    return (
      <DarkModeProvider>
        <BrowserRouter>
          <AppErrorBoundary>
            <Suspense fallback={<FullPageLoader />}>
              <Routes>
                <Route path="/share/workout/:sessionId" element={<SharedWorkoutSession />} />
                <Route path="/roast/bean/:beanId" element={<PublicBeanPage />} />
                <Route path="/roast/blend/:containerId" element={<PublicBlendPage />} />
              </Routes>
            </Suspense>
          </AppErrorBoundary>
        </BrowserRouter>
      </DarkModeProvider>
    );
  }

  if (loading) {
    return <FullPageLoader />;
  }

  if (!user) {
    return <Auth />;
  }

  const handleProfileComplete = async () => {
    setShowProfileSetup(false);
    await refetch();
  };

  const handleProfileSkip = () => {
    setShowProfileSetup(false);
  };

  return (
    <DarkModeProvider userId={user.id}>
      <BrowserRouter>
        <AppErrorBoundary>
          {/* Profile Setup Modal - Shows for new users */}
          {shouldShowSetup && (
            <Suspense fallback={null}>
              <ProfileSetup onComplete={handleProfileComplete} onSkip={handleProfileSkip} />
            </Suspense>
          )}

          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: 'var(--bg)',
                color: 'var(--text)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--space-4)',
                fontSize: 'var(--text-sm)',
              },
              success: {
                iconTheme: {
                  primary: 'var(--accent)',
                  secondary: 'var(--bg)',
                },
              },
              error: {
                iconTheme: {
                  primary: 'var(--error)',
                  secondary: 'var(--bg)',
                },
              },
            }}
          />
          <Suspense fallback={null}>
            <OfflineIndicator />
            <PWAInstallPrompt />
          </Suspense>
          <Suspense fallback={<FullPageLoader />}>
            <Routes>
              <Route
                element={
                  <div style={{ minHeight: '100vh', background: 'var(--bg)' }} className="pb-16 md:pb-0">
                    <Topbar />
                    <Outlet />
                    <MobileNav />
                  </div>
                }
              >
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<DashboardV2 />} />
                <Route path="/workouts">
                  <Route index element={<Navigate to="/workouts/log" replace />} />
                  <Route path="log" element={<WorkoutManager userId={user.id} />} />
                  <Route path="log/:sessionId" element={<WorkoutManager userId={user.id} />} />
                  <Route path="session/:sessionId" element={<WorkoutSessionDetails />} />
                  <Route path="hr-matching" element={<HeartRateMatching />} />
                  <Route path="create" element={<WorkoutProgrammerV2Page />} />
                  <Route path="program" element={<WorkoutProgrammerV2Page />} />
                  <Route path="prs" element={<PRsPage />} />
                  <Route path="history" element={<Navigate to="/workouts/log" replace />} />
                  <Route path="templates" element={<WorkoutTemplates userId={user.id} />} />
                  <Route path="analytics" element={<WorkoutAnalyticsPage userId={user.id} />} />
                  <Route path="leaderboards" element={<BenchmarkLeaderboards />} />
                  <Route path="blocks" element={<BlockAnalyzer />} />
                  <Route path="movements" element={<MovementManager userId={user.id} />} />
                  <Route path="movements/:movementId" element={<MovementHistoryPage userId={user.id} />} />
                  <Route path="programs">
                    <Route index element={<ProgramBrowsePage />} />
                    <Route path="create" element={<ProgramCreatePage />} />
                    <Route path="my-programs" element={<MyProgramsPage />} />
                    <Route path="calendar" element={<MultiProgramCalendar />} />
                    <Route path="workout/:workoutId" element={<ProgramWorkoutDetailPage />} />
                    <Route path=":programId" element={<ProgramDetailPage />} />
                    <Route path=":programId/edit" element={<ProgramEditPage />} />
                    <Route path=":programId/analytics" element={<ProgramAnalyticsPage />} />
                    <Route path=":programId/workouts/:workoutId" element={<ProgramWorkoutDetailPage />} />
                  </Route>
                </Route>
                <Route path="/benchmarks">
                  <Route index element={<BenchmarksPage />} />
                  <Route path=":benchmarkId" element={<BenchmarkDetailPage />} />
                </Route>
                <Route path="/social">
                  <Route index element={<Navigate to="/social/feed" replace />} />
                  <Route path="feed" element={<ActivityFeedPage />} />
                  <Route path="discover" element={<DiscoverUsersPage />} />
                  <Route path="profile/:username" element={<UserProfilePage />} />
                  <Route path="privacy" element={<Navigate to="/settings" replace />} />
                </Route>
                <Route path="/gyms">
                  <Route index element={<GymBrowsePage />} />
                  <Route path="create" element={<GymCreatePage />} />
                  <Route path=":slug" element={<GymProfilePage />} />
                  <Route path=":slug/settings" element={<GymSettingsPage />} />
                </Route>
                <Route path="/supplements">
                  <Route index element={<Navigate to="/daily#supplements" replace />} />
                  <Route path="adherence" element={<SupplementAdherence />} />
                  <Route path="manager" element={<SupplementManager userId={user.id} />} />
                </Route>
                <Route path="/wearables" element={<WearablesSyncPage userId={user.id} />} />
                <Route path="/wearables/dashboard" element={<WearablesDashboard userId={user.id} />} />
                <Route path="/history" element={<RecentWorkouts userId={user.id} />} />
                <Route path="/daily-entry" element={<Navigate to="/daily#recovery" replace />} />
                <Route path="/daily" element={<DailyTrackingPage />} />
                <Route path="/settings" element={<UserSettings userId={user.id} />} />
                <Route path="/calendar" element={<PageContainer><TrainingCalendar userId={user.id} /></PageContainer>} />
                <Route path="/insights" element={<PageContainer><Insights userId={user.id} /></PageContainer>} />
                <Route path="/long-horizon" element={<PageContainer><LongHorizonPage userId={user.id} /></PageContainer>} />
                <Route path="/performance-delta" element={<PageContainer><PerformanceDeltaPage userId={user.id} /></PageContainer>} />
                <Route path="/technique" element={<PageContainer><TechniqueAnalyzerPage userId={user.id} /></PageContainer>} />
                <Route path="/nutrition">
                  <Route index element={<Navigate to="/daily#nutrition" replace />} />
                  <Route path="daily" element={<Navigate to="/daily#nutrition" replace />} />
                  <Route path="block" element={<PageContainer><BlockNutritionPage userId={user.id} /></PageContainer>} />
                  <Route path="meals" element={<PageContainer><MealTemplatesPage userId={user.id} /></PageContainer>} />
                </Route>
                <Route path="/competition">
                  <Route index element={<PageContainer><CompetitionManagerPage userId={user.id} /></PageContainer>} />
                  <Route path="create" element={<PageContainer><CompetitionCreatePage userId={user.id} /></PageContainer>} />
                  <Route path="leaderboard" element={<PageContainer><LeaderboardPage userId={user.id} /></PageContainer>} />
                  <Route path=":id" element={<PageContainer><CompetitionEventPage userId={user.id} /></PageContainer>} />
                  <Route path=":id/review" element={<PageContainer><CompetitionReviewPage userId={user.id} /></PageContainer>} />
                </Route>
                <Route path="/coach" element={<PageContainer><CoachPage userId={user.id} /></PageContainer>} />
                <Route path="/coach-mode" element={<PageContainer><AdaptiveCoachPage userId={user.id} /></PageContainer>} />
                <Route path="/realtime" element={<PageContainer><RealtimeSessionPage userId={user.id} /></PageContainer>} />
                <Route path="/movement-bias" element={<PageContainer><MovementBiasPage userId={user.id} /></PageContainer>} />
                <Route path="/reports" element={<PageContainer><MonthlyReportsPage userId={user.id} /></PageContainer>} />
                <Route path="/roast/*" element={
                  <Suspense fallback={<FullPageLoader />}>
                    <RoastingApp />
                  </Suspense>
                } />
              </Route>
            </Routes>
          </Suspense>
        </AppErrorBoundary>
      </BrowserRouter>
    </DarkModeProvider>
  );
}
